<template>
  <div class="d-flex justify-center flex-column align-center">
    <div v-if="mockupAlert.value">
      <v-alert
        class="my-2 text-center"
        type="info"
        variant="tonal"
        border="start"
      >
        {{ $t("strings.mockup-alert") }}
      </v-alert>
    </div>
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script>
import { ref, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Chart as ChartJS, registerables } from "chart.js/auto";
import annotationPlugin from "chartjs-plugin-annotation";
import { applyTransparencyToDatasets } from "@/utils/chartUtils.js";

ChartJS.register(...registerables, annotationPlugin);

export default {
  props: {
    data: Object,
    type: {
      type: String,
      default: "bar",
    },
    targetValue: [Number, String],
    mockupAlert: Object,
    isQualitative: Boolean,
    horizontal: Boolean,
  },
  setup(props) {
    const { t } = useI18n();
    const canvas = ref(null);
    const mockupAlert = ref(false);
    let chartInstance = null;
    
    const chartOptions = {
      plugins: {
        legend: {
          position:
            props.type !== "doughnut" && props.type !== "polarArea"
              ? "top"
              : "right",
        },

        // Annotation for the line that marks the target value
        annotation: {
          annotations:
            !props.isQualitative && props.type !== "doughnut"
              ? {
                  line1: {
                    type: "line",
                    ...(props.horizontal
                      ? { xMin: props.targetValue, xMax: props.targetValue }
                      : { yMin: props.targetValue, yMax: props.targetValue }),
                    borderColor: "#4CAF50",
                    borderWidth: 2,
                    label: {
                      content: `Target: ${props.targetValue}`,
                      enabled: true,
                      position: "end",
                    },
                  },
                }
              : {},
        },
        tooltip: {
          callbacks: props.isQualitative
            ? {
                label: function (context) {
                  const label = context.label || "";
                  // Per horizontal (indexAxis: 'y'), il valore è in parsed.x
                  // Per vertical (indexAxis: 'x'), il valore è in parsed.y
                  const value = props.horizontal 
                    ? context.parsed.x 
                    : (context.parsed.y || context.parsed.r || context.parsed);
                  const isTarget = label === props.targetValue;
                  return `${label}: ${value}${isTarget ? " (Target)" : ""}`;
                },
              }
            : undefined,
        },
      },
      maintainAspectRatio: true,
      responsive: true,
      aspectRatio: 4,
      indexAxis: props.horizontal ? "y" : "x",
      scales:
        props.type !== "doughnut" &&
        props.type !== "polarArea" &&
        props.type !== "radar"
          ? {
              x: {
                stacked: false,
                title: {
                  display: props.isQualitative && props.horizontal,
                  text: props.isQualitative && props.horizontal
                    ? t("labels.frequency")
                    : undefined,
                },
                beginAtZero: true,
              },
              y: {
                stacked: false,
                title: {
                  display: props.isQualitative && !props.horizontal,
                  text: props.isQualitative && !props.horizontal
                    ? t("labels.frequency")
                    : undefined,
                },
                ticks: props.isQualitative && props.horizontal ? {
                  callback: (value) => {
                    const label = props.data.labels && props.data.labels[value];
                    if (!label) return "";
                    const words = label.includes(" ")
                      ? label.split(", ")
                      : [label];
                    return words;
                  },
                  maxRotation: 0,
                  minRotation: 0,
                } : {
                  maxRotation: 75,
                  minRotation: 45,
                },
                beginAtZero: true,
              },
            }
          : {},
    };
    const initializeChart = () => {
      if (chartInstance) {
        chartInstance.destroy();
      }
      
      // Apply transparency to dataset colors
      const dataWithTransparency = {
        ...props.data,
        datasets: applyTransparencyToDatasets(props.data.datasets)
      };
      
      chartInstance = new ChartJS(canvas.value, {
        type: props.type === "area" ? "line" : props.type,
        data: dataWithTransparency,
        options: {
          ...chartOptions,
          fill: props.type === "area",
        },
      });
    };

    onMounted(() => {
      initializeChart();
      mockupAlert.value = props.mockupAlert;
    });

    watch(
      () => props.data,
      () => {
        if (chartInstance) {
          // Apply transparency to updated data
          const dataWithTransparency = {
            ...props.data,
            datasets: applyTransparencyToDatasets(props.data.datasets)
          };
          chartInstance.data = dataWithTransparency;
          chartInstance.update();
        }
      },
    );

    watch(
      () => props.type,
      () => {
        initializeChart();
      },
    );

    return {
      canvas,
    };
  },
};
</script>

<style scoped>
canvas {
  max-width: 100%;
  height: auto; /* You can adjust this value if needed */
}
</style>
